// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: string;
};

/**
 * AcpExportStatus
 */
export enum AcpExportStatus {
    REQUESTED = 'REQUESTED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR'
}

/**
 * AdoptionEntry
 *
 * One kind of Reedy-generated content the publisher adopted into this
 * import -- generated content that reappears in the imported ONIX. `adopted`
 * lists all adopted items (keyword phrases or Thema codes); it is empty for
 * descriptions, which `count` alone conveys.
 */
export type AdoptionEntry = {
    kind: ContentKind;
    /**
     * Count
     */
    count: number;
    /**
     * Adopted
     */
    adopted?: Array<string>;
};

/**
 * ChunkStatus
 *
 * Chunk status codes for sorting and extensibility (lower = higher priority)
 */
export enum ChunkStatus {
    _100 = 100,
    _200 = 200,
    _300 = 300,
    _400 = 400,
    _500 = 500
}

/**
 * ContentKind
 *
 * The metadata kinds Reedy generates and an ONIX import can change or
 * adopt.
 */
export enum ContentKind {
    KEYWORDS = 'keywords',
    THEMA_CODES = 'themaCodes',
    DESCRIPTION = 'description'
}

/**
 * EasterOffsetRule
 */
export type EasterOffsetRule = {
    /**
     * Type
     */
    type: 'easter_offset';
    date_kind: SeasonalEventDateKind;
    /**
     * Days
     */
    days: number;
};

/**
 * FileType
 */
export enum FileType {
    ACP_EXPORT = 'ACP_EXPORT',
    EVALUATION_REPORT = 'EVALUATION_REPORT',
    MANUAL_ONIX_IMPORT = 'MANUAL_ONIX_IMPORT',
    MANUSCRIPT = 'MANUSCRIPT',
    MANUSCRIPT_VERSION = 'MANUSCRIPT_VERSION',
    ONIX_CHUNK = 'ONIX_CHUNK',
    ONIX_EXPORT = 'ONIX_EXPORT',
    ONIX_IMPORT = 'ONIX_IMPORT',
    ONIX_IMPORT_REPORT = 'ONIX_IMPORT_REPORT',
    TREND = 'TREND',
    UNKNOWN = 'UNKNOWN'
}

/**
 * FirestoreAcpExport
 */
export type FirestoreAcpExport = {
    /**
     * Id
     */
    id: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Userid
     */
    userId: string;
    status?: AcpExportStatus;
    /**
     * Count
     */
    count?: number | null;
    /**
     * Storagepath
     */
    storagePath?: string | null;
    /**
     * Createdat
     */
    createdAt?: Date;
    /**
     * Updatedat
     */
    updatedAt?: Date | null;
};

/**
 * FirestoreFile
 */
export type FirestoreFile = {
    /**
     * Updatedat
     */
    updatedAt: Date;
    /**
     * Createdat
     */
    createdAt: Date | null;
    /**
     * Filename
     */
    filename: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Bucketname
     */
    bucketName: string;
    /**
     * Size
     */
    size?: number | null;
    mode: FileType;
    /**
     * Path
     */
    path: string;
};

/**
 * FirestoreGenerationRequestDescriptions
 */
export type FirestoreGenerationRequestDescriptions = {
    /**
     * Id
     */
    id: string;
    /**
     * Userid
     */
    userId: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Entityid
     */
    entityId?: string | null;
    status?: FirestoreGenerationStatus;
    statusCode?: FirestoreGenerationStatusCode | null;
    /**
     * Statusmessage
     */
    statusMessage?: string | null;
    /**
     * Createdat
     */
    createdAt: Date;
    /**
     * Updatedat
     */
    updatedAt?: Date | null;
    data: FirestoreGenerationRequestDescriptionsData;
};

/**
 * FirestoreGenerationRequestDescriptionsData
 */
export type FirestoreGenerationRequestDescriptionsData = {
    /**
     * Hash
     */
    hash?: string | null;
    /**
     * Specialrequest
     */
    specialRequest?: string | null;
    /**
     * Targetgroup
     */
    targetGroup?: string | null;
};

/**
 * FirestoreGenerationRequestEvaluation
 */
export type FirestoreGenerationRequestEvaluation = {
    /**
     * Id
     */
    id: string;
    /**
     * Userid
     */
    userId: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Entityid
     */
    entityId?: string | null;
    status?: FirestoreGenerationStatus;
    statusCode?: FirestoreGenerationStatusCode | null;
    /**
     * Statusmessage
     */
    statusMessage?: string | null;
    /**
     * Createdat
     */
    createdAt: Date;
    /**
     * Updatedat
     */
    updatedAt?: Date | null;
    data: FirestoreGenerationRequestEvaluationData;
};

/**
 * FirestoreGenerationRequestEvaluationData
 */
export type FirestoreGenerationRequestEvaluationData = {
    /**
     * Mode
     */
    mode?: 'SYNC' | 'ASYNC';
    /**
     * Requestedproducts
     */
    requestedProducts: 'ALL' | Array<string>;
    /**
     * Async Batch Id
     */
    async_batch_id?: string | null;
    async_batch_status?: OpenAiStatus | null;
    /**
     * File
     */
    file?: {
        [key: string]: unknown;
    } | null;
    /**
     * Last Evaluated Identifier
     */
    last_evaluated_identifier?: string | null;
};

/**
 * FirestoreGenerationStatus
 */
export enum FirestoreGenerationStatus {
    IMPORTED = 'IMPORTED',
    REQUESTED = 'REQUESTED',
    GENERATING = 'GENERATING',
    GENERATED = 'GENERATED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
    ERROR = 'ERROR'
}

/**
 * FirestoreGenerationStatusCode
 */
export enum FirestoreGenerationStatusCode {
    _100 = '100',
    _102 = '102',
    _200 = '200',
    _402 = '402',
    _422 = '422',
    _451 = '451',
    _499 = '499',
    _500 = '500'
}

/**
 * FirestoreManuscriptProcessingReporting
 *
 * One entry per received manuscript version, driving the admin panel.
 */
export type FirestoreManuscriptProcessingReporting = {
    /**
     * Id
     */
    id: string;
    organisation: FirestoreOrganisationReference;
    status: ManuscriptProcessingStatus;
    /**
     * Filename
     */
    fileName: string;
    /**
     * Isbn
     */
    isbn: string;
    /**
     * Contenthash
     */
    contentHash: string;
    /**
     * Objectpath
     */
    objectPath: string;
    /**
     * Contentclaimed
     */
    contentClaimed?: boolean;
    /**
     * Duplicateofreportid
     */
    duplicateOfReportId?: string | null;
    /**
     * Productid
     */
    productId?: string | null;
    /**
     * Producttitle
     */
    productTitle?: string | null;
    /**
     * Workid
     */
    workId?: string | null;
    /**
     * Jobid
     */
    jobId?: string | null;
    /**
     * Submissiontaskid
     */
    submissionTaskId?: string | null;
    /**
     * Semanticdataid
     */
    semanticDataId?: string | null;
    error?: ManuscriptProcessingError | null;
    /**
     * Createdat
     */
    createdAt?: Date;
    /**
     * Updatedat
     */
    updatedAt?: Date | null;
};

/**
 * FirestoreOnixExport
 */
export type FirestoreOnixExport = {
    /**
     * Id
     */
    id: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Productids
     */
    productIds?: Array<string>;
    status?: OnixExportStatus;
    /**
     * Userid
     */
    userId: string;
    /**
     * Createdat
     */
    createdAt?: Date | null;
    /**
     * Updatedat
     */
    updatedAt?: Date | null;
    /**
     * Filename
     */
    fileName?: string | null;
};

/**
 * FirestoreOrganisation
 */
export type FirestoreOrganisation = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Parent Organisation
     */
    parent_organisation?: string | null;
    /**
     * Emails
     */
    emails?: Array<string> | null;
    /**
     * Styles
     */
    styles?: Array<string> | {
        [key: string]: Array<string>;
    } | null;
    /**
     * Targets
     */
    targets?: Array<string> | null;
    /**
     * Targetgroups
     */
    targetGroups?: Array<FirestoreOrganisationTargetGroups> | null;
    /**
     * Limitapirequests
     */
    limitApiRequests?: Array<FirestoreOrganisationLimitApiRequestsSettings> | null;
    /**
     * Limitgenerations
     */
    limitGenerations?: Array<FirestoreOrganisationLimitGenerationsSettings> | null;
    /**
     * Disablepromptguard
     */
    disablePromptGuard?: boolean | null;
    settings?: FirestoreOrganisationSettings;
    /**
     * Descriptionsdefault
     */
    descriptionsDefault?: string | null;
    /**
     * Descriptionstypes
     */
    descriptionsTypes?: {
        [key: string]: number;
    } | null;
    /**
     * Createdat
     */
    createdAt?: Date;
    /**
     * Deletedat
     */
    deletedAt?: Date | null;
    /**
     * Harddeletedat
     */
    hardDeletedAt?: Date | null;
};

/**
 * FirestoreOrganisationAcpSettings
 */
export type FirestoreOrganisationAcpSettings = {
    /**
     * Feed Id
     */
    feed_id?: string | null;
    /**
     * Account Id
     */
    account_id?: string | null;
    /**
     * Target Merchant
     */
    target_merchant?: string | null;
    /**
     * Target Country
     */
    target_country?: string | null;
};

/**
 * FirestoreOrganisationLanguage
 */
export enum FirestoreOrganisationLanguage {
    EN_GB = 'en-GB',
    EN_US = 'en-US',
    DE_DE = 'de-DE'
}

/**
 * FirestoreOrganisationLimitApiRequestsSettings
 */
export type FirestoreOrganisationLimitApiRequestsSettings = {
    /**
     * Interval
     */
    interval: number;
    /**
     * Limit
     */
    limit: number;
};

/**
 * FirestoreOrganisationLimitGenerationsSettings
 */
export type FirestoreOrganisationLimitGenerationsSettings = {
    /**
     * Interval
     */
    interval: number;
    /**
     * Limit
     */
    limit: number;
};

/**
 * FirestoreOrganisationMarketplace
 */
export enum FirestoreOrganisationMarketplace {
    UK = 'UK',
    US = 'US',
    GER = 'GER',
    AU = 'AU',
    NONE = 'NONE'
}

/**
 * FirestoreOrganisationReference
 */
export type FirestoreOrganisationReference = {
    /**
     * Id
     */
    id: string;
};

/**
 * FirestoreOrganisationSalesDataLake
 *
 * Which sales data lake an organisation reads from.
 */
export enum FirestoreOrganisationSalesDataLake {
    MMDE = 'MMDE',
    PMUK = 'PMUK',
    MMUS = 'MMUS',
    DISABLED = 'DISABLED'
}

/**
 * FirestoreOrganisationSettings
 */
export type FirestoreOrganisationSettings = {
    /**
     * Inactiveproductstatus
     */
    inactiveProductStatus?: Array<ProductAvailabilityCodeLiteral>;
    /**
     * Excludenoavailabilitycode
     */
    excludeNoAvailabilityCode?: boolean;
    /**
     * Canseeamazonsearchterms
     */
    canSeeAmazonSearchTerms?: boolean | null;
    marketplace?: FirestoreOrganisationMarketplace | null;
    language?: FirestoreOrganisationLanguage | null;
    acpSettings?: FirestoreOrganisationAcpSettings | null;
    salesDataLake?: FirestoreOrganisationSalesDataLake;
};

/**
 * FirestoreOrganisationTargetGroups
 */
export type FirestoreOrganisationTargetGroups = {
    /**
     * Name
     */
    name: string;
    /**
     * Description
     */
    description: string;
};

/**
 * FirestoreProduct
 */
export type FirestoreProduct = {
    /**
     * Id
     */
    id: string;
    /**
     * Identifier
     */
    identifier: string;
    /**
     * Identifiertype
     */
    identifierType?: string | null;
    /**
     * Identifiers
     */
    identifiers?: Array<FirestoreProductIdentifier>;
    /**
     * Workid
     */
    workId?: string | null;
    importData?: FirestoreProductImportData | null;
    /**
     * Title
     */
    title: string;
    /**
     * Descriptions
     */
    descriptions?: Array<FirestoreProductDescription> | null;
    /**
     * Subtitles
     */
    subtitles?: Array<FirestoreProductSubtitleCandidate>;
    /**
     * Biography
     */
    biography?: string | null;
    /**
     * Author
     */
    author?: Array<string>;
    /**
     * Genre
     */
    genre?: Array<string>;
    /**
     * Keywordcount
     */
    keywordCount?: number;
    /**
     * Language
     */
    language: string;
    /**
     * Languageoriginal
     */
    languageOriginal: string;
    organisation: FirestoreOrganisationReference;
    productGroup: ProductFormCodeLiteral;
    productGroupDescription: ProductFormDescLiteral;
    /**
     * Publisher
     */
    publisher: string;
    /**
     * Publishers
     */
    publishers?: Array<FirestoreProductPublisher>;
    /**
     * Imprints
     */
    imprints?: Array<string>;
    /**
     * Publishedat
     */
    publishedAt: Date | null;
    /**
     * Createdat
     */
    createdAt: Date;
    /**
     * Updatedat
     */
    updatedAt: Date;
    subjects?: FirestoreProductSubjects | null;
    /**
     * Forcesynctriggeredat
     */
    forceSyncTriggeredAt?: Date | null;
    categorization?: FirestoreProductCategorization | null;
    sourceType?: ProductSource;
    manuscript?: FirestoreProductManuscript | null;
    actions?: FirestoreProductActions;
    /**
     * Iseligiblesearch
     */
    isEligibleSearch?: boolean;
    /**
     * Iseligiblecheckout
     */
    isEligibleCheckout?: boolean;
    /**
     * Supplydetails
     */
    supplyDetails?: Array<FirestoreProductSupplyDetail>;
    dimensions?: FirestoreProductDimensions | null;
    ageRanges?: FirestoreProductAgeRanges | null;
    images?: FirestoreProductImages;
    /**
     * Quotes
     */
    quotes?: Array<FirestoreProductQuote>;
    series?: FirestoreProductSeries;
};

/**
 * FirestoreProductActions
 */
export type FirestoreProductActions = {
    /**
     * Active
     */
    active?: boolean;
    /**
     * Errored
     */
    errored?: boolean;
    keywords?: FirestoreProductActionsKeywords;
    descriptions?: FirestoreProductActionsDescriptions | null;
    thema?: FirestoreProductActionsThema | null;
    manuscript?: FirestoreProductActionsManuscript | null;
    subtitle?: FirestoreProductActionsSubtitle | null;
};

/**
 * FirestoreProductActionsDescriptions
 */
export type FirestoreProductActionsDescriptions = {
    /**
     * Active
     */
    active?: boolean;
    /**
     * Errored
     */
    errored?: boolean;
    /**
     * Actions
     */
    actions?: {
        [key: string]: FirestoreProductActionsDescriptionsActions;
    };
};

/**
 * FirestoreProductActionsDescriptionsActions
 */
export type FirestoreProductActionsDescriptionsActions = {
    generate?: FirestoreProductActionsValue;
};

/**
 * FirestoreProductActionsKeywords
 */
export type FirestoreProductActionsKeywords = {
    /**
     * Active
     */
    active?: boolean;
    /**
     * Errored
     */
    errored?: boolean;
    actions?: FirestoreProductActionsKeywordsActions;
};

/**
 * FirestoreProductActionsKeywordsActions
 */
export type FirestoreProductActionsKeywordsActions = {
    rank?: FirestoreProductActionsValue | null;
    generate?: FirestoreProductActionsValue | null;
    autoFill?: FirestoreProductActionsValue | null;
};

/**
 * FirestoreProductActionsManuscript
 */
export type FirestoreProductActionsManuscript = {
    /**
     * Active
     */
    active?: boolean;
    /**
     * Errored
     */
    errored?: boolean;
    actions?: FirestoreProductActionsManuscriptActions | null;
};

/**
 * FirestoreProductActionsManuscriptActions
 */
export type FirestoreProductActionsManuscriptActions = {
    analyse?: FirestoreProductActionsValue | null;
};

/**
 * FirestoreProductActionsSubtitle
 */
export type FirestoreProductActionsSubtitle = {
    /**
     * Active
     */
    active?: boolean;
    /**
     * Errored
     */
    errored?: boolean;
    actions?: FirestoreProductActionsSubtitleActions | null;
};

/**
 * FirestoreProductActionsSubtitleActions
 */
export type FirestoreProductActionsSubtitleActions = {
    generate?: FirestoreProductActionsValue | null;
};

/**
 * FirestoreProductActionsThema
 */
export type FirestoreProductActionsThema = {
    /**
     * Active
     */
    active?: boolean;
    /**
     * Errored
     */
    errored?: boolean;
    actions?: FirestoreProductActionsThemaActions | null;
};

/**
 * FirestoreProductActionsThemaActions
 */
export type FirestoreProductActionsThemaActions = {
    generate?: FirestoreProductActionsValue | null;
};

/**
 * FirestoreProductActionsValue
 */
export type FirestoreProductActionsValue = {
    status?: FirestoreGenerationStatus | null;
    statusCode?: FirestoreGenerationStatusCode | null;
    /**
     * Msg
     */
    msg?: string;
};

/**
 * FirestoreProductAgeRange
 *
 * A normalized audience age range; either bound may be open-ended.
 */
export type FirestoreProductAgeRange = {
    /**
     * Gte
     */
    gte?: number | null;
    /**
     * Lte
     */
    lte?: number | null;
};

/**
 * FirestoreProductAgeRanges
 *
 * ONIX List 30 qualifiers 16/17/18 (see ``get_age_ranges``).
 */
export type FirestoreProductAgeRanges = {
    interestAgeMonths?: FirestoreProductAgeRange | null;
    interestAgeYears?: FirestoreProductAgeRange | null;
    readingAgeYears?: FirestoreProductAgeRange | null;
};

/**
 * FirestoreProductBisacSubject
 *
 * A BISAC code on a product, ordered by list position until the user selects it.
 *
 * A ``MANUAL`` entry is always ``final``: it exists only because the user selected
 * it, and an entry that is neither ``inSource`` nor ``final`` is deleted on the next
 * import.
 */
export type FirestoreProductBisacSubject = {
    /**
     * Id
     *
     * The identifier of the product's BISAC subject
     */
    id: string;
    /**
     * Subjectcode
     *
     * The BISAC subject code
     */
    subjectCode?: string;
    /**
     * Subjectheadingtext
     */
    subjectHeadingText?: string | null;
    /**
     * Who created the entry; never rewritten once set
     */
    source?: SubjectSource;
    /**
     * Insource
     *
     * Whether the latest imported ONIX file still carries the code
     */
    inSource?: boolean;
    /**
     * Final
     *
     * Whether the user selected the code
     */
    final?: boolean;
    /**
     * Orderindex
     *
     * Position among the codes the user selected
     */
    orderIndex?: number | null;
    /**
     * Seq
     *
     * Position of the code in the BISAC catalogue; set only when subjectHeadingText is the catalogue heading
     */
    seq?: number | null;
};

/**
 * FirestoreProductCategorization
 */
export type FirestoreProductCategorization = {
    /**
     * Fiction
     */
    fiction?: boolean;
    /**
     * Nonfiction
     */
    nonFiction?: boolean;
    /**
     * Children
     */
    children?: boolean;
    /**
     * Updatedat
     */
    updatedAt?: Date | null;
    /**
     * Inferredfrom
     */
    inferredFrom?: 'THEMA' | 'LLM' | null;
};

/**
 * FirestoreProductDescription
 */
export type FirestoreProductDescription = {
    /**
     * Hash
     */
    hash?: string;
    /**
     * Default
     */
    default?: boolean;
    /**
     * Textgenerated
     */
    textGenerated?: string;
    /**
     * Text
     */
    text?: string;
    /**
     * Textformat
     */
    textFormat?: string;
    /**
     * Texttype
     */
    textType?: string;
    /**
     * Audiences
     */
    audiences?: Array<string>;
};

/**
 * FirestoreProductDimensions
 *
 * Lengths in mm, weight in grams; keys match ``normalize_dimensions`` output.
 */
export type FirestoreProductDimensions = {
    /**
     * Width
     */
    width?: number | null;
    /**
     * Height
     */
    height?: number | null;
    /**
     * Thickness
     */
    thickness?: number | null;
    /**
     * Weight
     */
    weight?: number | null;
    /**
     * Pages
     */
    pages?: number | null;
    /**
     * Flaps
     */
    flaps?: number | null;
};

/**
 * FirestoreProductIdentifier
 */
export type FirestoreProductIdentifier = {
    /**
     * Type
     */
    type: string;
    /**
     * Value
     */
    value: string;
};

/**
 * FirestoreProductImages
 */
export type FirestoreProductImages = {
    /**
     * Frontcoverurl
     */
    frontCoverUrl?: string;
    /**
     * Backcoverurl
     */
    backCoverUrl?: string;
    /**
     * Supportingresources
     */
    supportingResources?: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * FirestoreProductImport
 */
export type FirestoreProductImport = {
    /**
     * Id
     */
    id: string | null;
    baseProduct: FirestoreProductImportBaseProductData;
    product: FirestoreProductImportProductOnixV3Data;
    importData: FirestoreProductImportImportData;
    header: FirestoreProductImportHeaderData;
};

/**
 * FirestoreProductImportBaseProductData
 */
export type FirestoreProductImportBaseProductData = {
    /**
     * Titles
     */
    titles: Array<FirestoreProductImportBaseProductDataTitleItem>;
    /**
     * Texts
     */
    texts: Array<FirestoreProductImportBaseProductDataText>;
    /**
     * Contributors
     */
    contributors: Array<FirestoreProductImportBaseProductDataContributorItem>;
    subjects: FirestoreProductImportBaseProductDataSubjects;
    /**
     * Publishers
     */
    publishers?: Array<FirestoreProductImportBaseProductDataPublisherItem> | null;
    /**
     * Salesrightss
     */
    salesRightss: Array<FirestoreProductImportBaseProductDataSalesRights> | null;
    /**
     * Imprints
     */
    imprints: Array<FirestoreProductImportBaseProductDataImprints>;
    /**
     * Collections
     */
    collections: Array<FirestoreProductImportBaseProductDataCollections>;
    description: FirestoreProductImportBaseProductDataDescription;
    /**
     * Supplydetails
     */
    supplyDetails?: Array<FirestoreProductImportBaseProductDataSupplyDetailsItem> | null;
    info: FirestoreProductImportBaseProductDataInfo;
    publishingDetails: FirestoreProductImportBaseProductDataPublishingDetails;
};

/**
 * FirestoreProductImportBaseProductDataCollectionSeriesIdentifier
 */
export type FirestoreProductImportBaseProductDataCollectionSeriesIdentifier = {
    /**
     * Collectionidtype
     */
    collectionIDType?: string | null;
    /**
     * Idtypename
     */
    idTypeName?: string | null;
    /**
     * Idvalue
     */
    idValue?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataCollectionTitle
 */
export type FirestoreProductImportBaseProductDataCollectionTitle = {
    /**
     * Titletype
     */
    titleType?: string | null;
    /**
     * Titletext
     */
    titleText?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataCollections
 */
export type FirestoreProductImportBaseProductDataCollections = {
    /**
     * Maintitle
     */
    mainTitle?: string | null;
    /**
     * Numberwithinseries
     */
    numberWithinSeries?: string | null;
    /**
     * Seriesidentifiers
     */
    seriesIdentifiers?: Array<FirestoreProductImportBaseProductDataCollectionSeriesIdentifier> | null;
    /**
     * Titles
     */
    titles?: Array<FirestoreProductImportBaseProductDataCollectionTitle> | null;
    /**
     * Contributors
     */
    contributors?: Array<FirestoreProductImportBaseProductDataContributorItem> | null;
};

/**
 * FirestoreProductImportBaseProductDataContributorItem
 */
export type FirestoreProductImportBaseProductDataContributorItem = {
    /**
     * Contributorroles
     */
    contributorRoles?: Array<string> | null;
    /**
     * Sequencenumber
     */
    sequenceNumber?: string | null;
    /**
     * Personname
     */
    personName?: string | null;
    /**
     * Personnameinverted
     */
    personNameInverted?: string | null;
    /**
     * Biographicalnote
     */
    biographicalNote?: string | null;
    /**
     * Personnamekey
     */
    personNameKey?: string | null;
    /**
     * Personnamebeforekey
     */
    personNameBeforeKey?: string | null;
    /**
     * Corporatename
     */
    corporateName?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataDescription
 */
export type FirestoreProductImportBaseProductDataDescription = {
    /**
     * Productform
     */
    productForm?: string | null;
    /**
     * Productformdetail
     */
    productFormDetail?: string | null;
    /**
     * Numberofpages
     */
    numberOfPages?: string | null;
    /**
     * Editiontype
     */
    editionType?: string | null;
    /**
     * Editionnumber
     */
    editionNumber?: number | null;
    /**
     * Audiencedescription
     */
    audienceDescription?: string | null;
    /**
     * Languages
     */
    languages: Array<FirestoreProductImportBaseProductDataDescriptionLanguage>;
    /**
     * Audiences
     */
    audiences: Array<FirestoreProductImportBaseProductDataDescriptionAudiencesItem>;
    audienceRange?: FirestoreProductImportBaseProductDataDescriptionAudienceRange | null;
};

/**
 * FirestoreProductImportBaseProductDataDescriptionAudienceRange
 */
export type FirestoreProductImportBaseProductDataDescriptionAudienceRange = {
    /**
     * Audiencerangequalifier
     */
    audienceRangeQualifier: string;
    /**
     * Audiencerangevalues
     */
    audienceRangeValues: Array<string>;
    /**
     * Audiencerangeprecisions
     */
    audienceRangePrecisions: Array<string>;
};

/**
 * FirestoreProductImportBaseProductDataDescriptionAudiencesItem
 */
export type FirestoreProductImportBaseProductDataDescriptionAudiencesItem = {
    /**
     * Audiencecodetype
     */
    audienceCodeType: string;
    /**
     * Audiencecodevalue
     */
    audienceCodeValue: string;
    /**
     * Audiencecodetypename
     */
    audienceCodeTypeName?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataDescriptionLanguage
 */
export type FirestoreProductImportBaseProductDataDescriptionLanguage = {
    /**
     * Languagerole
     */
    languageRole: string;
    /**
     * Languagecode
     */
    languageCode: string;
};

/**
 * FirestoreProductImportBaseProductDataImprints
 */
export type FirestoreProductImportBaseProductDataImprints = {
    /**
     * Imprintname
     */
    imprintName: string;
};

/**
 * FirestoreProductImportBaseProductDataInfo
 */
export type FirestoreProductImportBaseProductDataInfo = {
    /**
     * Productids
     */
    productIds: Array<FirestoreProductImportBaseProductDataInfoProductIdItem>;
    /**
     * Recordreference
     */
    recordReference: string;
    /**
     * Notificationtype
     */
    notificationType: string;
};

/**
 * FirestoreProductImportBaseProductDataInfoProductIdItem
 */
export type FirestoreProductImportBaseProductDataInfoProductIdItem = {
    /**
     * Productidtype
     */
    productIDType: string;
    /**
     * Idvalue
     */
    idValue: string;
    /**
     * Idtypename
     */
    idTypeName?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataPublisherItem
 */
export type FirestoreProductImportBaseProductDataPublisherItem = {
    /**
     * Publishername
     */
    publisherName?: string | null;
    /**
     * Publishingrole
     */
    publishingRole?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataPublishingDetails
 */
export type FirestoreProductImportBaseProductDataPublishingDetails = {
    /**
     * Publicationdate
     */
    publicationDate?: string | null;
    /**
     * Outofprintdate
     */
    outOfPrintDate?: string | null;
    /**
     * Countryofpublication
     */
    countryOfPublication?: string | null;
    /**
     * Cityofpublication
     */
    cityOfPublication?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataSalesRights
 */
export type FirestoreProductImportBaseProductDataSalesRights = {
    /**
     * Salesrightstype
     */
    salesRightsType: string;
    /**
     * Countries
     */
    countries?: Array<string> | null;
    /**
     * Regions
     */
    regions?: Array<string> | null;
    /**
     * Rightregions
     */
    rightRegions?: Array<string> | null;
    /**
     * Countriesexcluded
     */
    countriesExcluded?: Array<string> | null;
    /**
     * Regionsexcluded
     */
    regionsExcluded?: Array<string> | null;
};

/**
 * FirestoreProductImportBaseProductDataSubjects
 */
export type FirestoreProductImportBaseProductDataSubjects = {
    /**
     * Thema Subject Category
     */
    Thema_subject_category?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Keywords
     */
    Keywords?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bisac Subject Heading
     */
    BISAC_Subject_Heading?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Subject Category
     */
    BIC_subject_category?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Time Period Qualifier
     */
    BIC_time_period_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Thema Time Period Qualifier
     */
    Thema_time_period_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Thema Place Qualifier
     */
    Thema_place_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Dewey
     */
    Dewey?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Abridged Dewey
     */
    Abridged_Dewey?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Lc Classification
     */
    LC_classification?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Lc Subject Heading
     */
    LC_subject_heading?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nlm Classification
     */
    NLM_classification?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Mesh Heading
     */
    MeSH_heading?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nal Subject Heading
     */
    NAL_subject_heading?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Aat
     */
    AAT?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Udc
     */
    UDC?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bisac Regional Theme
     */
    BISAC_Regional_theme?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Geographical Qualifier
     */
    BIC_geographical_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Language Qualifier Language As Subject
     */
    BIC_language_qualifier_language_as_subject?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Educational Purpose Qualifier
     */
    BIC_educational_purpose_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Reading Level And Special Interest Qualifier
     */
    BIC_reading_level_and_special_interest_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ddc Sachgruppen Der Deutschen Nationalbibliografie
     */
    DDC_Sachgruppen_der_Deutschen_Nationalbibliografie?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Lc Fiction Genre Heading
     */
    LC_fiction_genre_heading?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Childrens Book Marketing Category
     */
    BIC_childrens_book_marketing_category?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bisac Merchandising Theme
     */
    BISAC_Merchandising_Theme?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Publishers Own Category Code
     */
    Publishers_own_category_code?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Proprietary Subject Scheme
     */
    Proprietary_subject_scheme?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Tabla De Materias Isbn
     */
    Tabla_de_materias_ISBN?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Warengruppen Systematik Des Deutschen Buchhandels
     */
    Warengruppen_Systematik_des_deutschen_Buchhandels?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Swd
     */
    SWD?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Themes Electre
     */
    Themes_Electre?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Clil Classification Thematique
     */
    CLIL_Classification_thematique?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Dnb Sachgruppen
     */
    DNB_Sachgruppen?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nugi
     */
    NUGI?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nur
     */
    NUR?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ecpa Christian Book Category
     */
    ECPA_Christian_Book_Category?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Siso
     */
    SISO?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Korean Decimal Classification Kdc
     */
    Korean_Decimal_Classification_KDC?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ddc Deutsch 22
     */
    DDC_Deutsch_22?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bokgrupper
     */
    Bokgrupper?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Varegrupper
     */
    Varegrupper?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Lreplaner Kl06
     */
    Lreplaner_KL06?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nippon Decimal Classification
     */
    Nippon_Decimal_Classification?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bsq
     */
    BSQ?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Anele Materias
     */
    ANELE_Materias?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Utdanningsprogram
     */
    Utdanningsprogram?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Programomrade
     */
    Programomrade?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Undervisningsmateriell
     */
    Undervisningsmateriell?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Norsk Ddk
     */
    Norsk_DDK?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Varugrupper
     */
    Varugrupper?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Sab
     */
    SAB?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Laromedelstyp
     */
    Laromedelstyp?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Forhandsbeskrivning
     */
    Forhandsbeskrivning?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Spanish Isbn Udc Subset
     */
    Spanish_ISBN_UDC_subset?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Eci Subject Categories
     */
    ECI_subject_categories?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Soggetto Cce
     */
    Soggetto_CCE?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Qualificatore Geografico Cce
     */
    Qualificatore_geografico_CCE?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Qualificatore Di Lingua Cce
     */
    Qualificatore_di_lingua_CCE?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Qualificatore Di Periodo Storico Cce
     */
    Qualificatore_di_periodo_storico_CCE?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Qualificatore Di Livello Scolastico Cce
     */
    Qualificatore_di_livello_scolastico_CCE?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Qualificatore Di Eta Di Lettura Cce
     */
    Qualificatore_di_eta_di_lettura_CCE?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Vds Bildungsmedien Facher
     */
    VdS_Bildungsmedien_Facher?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Fagkoder
     */
    Fagkoder?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Jel Classification
     */
    JEL_classification?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Csh
     */
    CSH?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Rvm
     */
    RVM?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ysa
     */
    YSA?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Allars
     */
    Allars?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ykl
     */
    YKL?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Musa
     */
    MUSA?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Cilla
     */
    CILLA?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Kaunokki
     */
    Kaunokki?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bella
     */
    Bella?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Yso
     */
    YSO?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Pto
     */
    PTO?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Suomalainen Kirja Alan Luokitus
     */
    Suomalainen_kirja_alan_luokitus?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Sears
     */
    Sears?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic E4L
     */
    BIC_E4L?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Csr
     */
    CSR?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Suomalainen Oppiaineluokitus
     */
    Suomalainen_oppiaineluokitus?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Japanese Book Trade C Code
     */
    Japanese_book_trade_C_Code?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Japanese Book Trade Genre Code
     */
    Japanese_book_trade_Genre_Code?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Fiktiivisen Aineiston Lisaluokitus
     */
    Fiktiivisen_aineiston_lisaluokitus?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Arabic Subject Heading Scheme
     */
    Arabic_Subject_heading_scheme?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Arabized Bic Subject Category
     */
    Arabized_BIC_subject_category?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Arabized Lc Subject Headings
     */
    Arabized_LC_subject_headings?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bibliotheca Alexandrina Subject Headings
     */
    Bibliotheca_Alexandrina_Subject_Headings?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Postal Code
     */
    Postal_code?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Geonames Id
     */
    GeoNames_ID?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Newbooks Subject Classification
     */
    NewBooks_Subject_Classification?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Chinese Library Classification
     */
    Chinese_Library_Classification?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ntcpdsac Classification
     */
    NTCPDSAC_Classification?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Season And Event Indicator
     */
    Season_and_Event_Indicator?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Gnd
     */
    GND?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bic Ukslc
     */
    BIC_UKSLC?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Thema Educational Purpose Qualifier
     */
    Thema_educational_purpose_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Thema Interest Age Special Interest Qualifier
     */
    Thema_interest_age_special_interest_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Thema Style Qualifier
     */
    Thema_style_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Thema Language Qualifier
     */
    Thema_language_qualifier?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Amnesord
     */
    Amnesord?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Statystyka Ksiazek Papierowych Mowionych I Elektronicznych
     */
    Statystyka_Ksiazek_Papierowych_Mowionych_I_Elektronicznych?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ccss
     */
    CCSS?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Rameau
     */
    Rameau?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nomenclature Discipline Scolaire
     */
    Nomenclature_discipline_scolaire?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Isic
     */
    ISIC?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Lc Childrens Subject Headings
     */
    LC_Childrens_Subject_Headings?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ny Laromedel
     */
    Ny_Laromedel?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Eurovoc
     */
    EuroVoc?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Bisg Educational Taxonomy
     */
    BISG_Educational_Taxonomy?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Keywords Not For Display
     */
    Keywords_not_for_display?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nomenclature Diplome
     */
    Nomenclature_Diplome?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Key Character Names
     */
    Key_character_names?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Key Place Names
     */
    Key_place_names?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Fast
     */
    FAST?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Ngss
     */
    NGSS?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Mvb Lesemotive
     */
    MVB_Lesemotive?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Lops21 Subject Module
     */
    LOPS21_Subject_module?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Lreplaner Lk20
     */
    Lreplaner_LK20?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Kompetansemal Lk20
     */
    Kompetansemal_LK20?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Kompetansemalsett Lk20
     */
    Kompetansemalsett_LK20?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Tverrfaglige Temaer Lk20
     */
    Tverrfaglige_temaer_LK20?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Clil Type Darticle Scolaire
     */
    CLIL_Type_darticle_scolaire?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Gar Type Pedagogique
     */
    GAR_Type_pedagogique?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Isced F
     */
    ISCED_F?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Klassifikationen Von Spielen Puzzles Und Spielwaren
     */
    Klassifikationen_von_Spielen_Puzzles_und_Spielwaren?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Nbvok Ntsf
     */
    NBVok_NTSF?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Jpro Genre
     */
    JPRO_Genre?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Kauno
     */
    KAUNO?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Slm
     */
    SLM?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    /**
     * Yso Places
     */
    YSO_places?: Array<FirestoreProductImportBaseProductDataSubjectsItem> | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportBaseProductDataSubjectsItem
 */
export type FirestoreProductImportBaseProductDataSubjectsItem = {
    /**
     * Subjectschemeidentifier
     */
    subjectSchemeIdentifier?: string | null;
    /**
     * Subjectheadingtext
     */
    subjectHeadingText?: string | null;
    /**
     * Subjectcode
     */
    subjectCode?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataSupplyDetailsItem
 */
export type FirestoreProductImportBaseProductDataSupplyDetailsItem = {
    /**
     * Suppliername
     */
    supplierName?: string | null;
    /**
     * Supplierrole
     */
    supplierRole?: string | null;
    /**
     * Availability
     */
    availability?: string | null;
    /**
     * Prices
     */
    prices?: Array<FirestoreProductImportBaseProductDataSupplyDetailsItemPrice> | null;
};

/**
 * FirestoreProductImportBaseProductDataSupplyDetailsItemPrice
 */
export type FirestoreProductImportBaseProductDataSupplyDetailsItemPrice = {
    /**
     * Pricetype
     */
    priceType?: string | null;
    /**
     * Priceamountasstr
     */
    priceAmountAsStr?: string | null;
    /**
     * Priceamount
     */
    priceAmount: number | null;
    /**
     * Currencycode
     */
    currencyCode?: string | null;
};

/**
 * FirestoreProductImportBaseProductDataText
 */
export type FirestoreProductImportBaseProductDataText = {
    /**
     * Default
     */
    default?: boolean | null;
    /**
     * Hash
     */
    hash?: string | null;
    /**
     * Texttype
     */
    textType?: string | null;
    /**
     * Text
     */
    text?: string | null;
    /**
     * Textformat
     */
    textFormat?: string | null;
    /**
     * Audiences
     */
    audiences?: Array<string> | null;
};

/**
 * FirestoreProductImportBaseProductDataTitleItem
 */
export type FirestoreProductImportBaseProductDataTitleItem = {
    /**
     * Titletype
     */
    titleType?: string | null;
    /**
     * Subtitle
     */
    subtitle?: string | null;
    /**
     * Titletext
     */
    titleText?: string | null;
    /**
     * Prefix
     */
    prefix?: string | null;
    /**
     * Titlewithoutprefix
     */
    titleWithoutPrefix?: string | null;
};

/**
 * FirestoreProductImportData
 */
export type FirestoreProductImportData = {
    /**
     * Importid
     */
    importId: string;
    /**
     * Productsimportid
     */
    productsImportId: string;
    /**
     * Objectname
     */
    objectName: string;
    /**
     * Foldername
     */
    folderName: string;
    /**
     * Filename
     */
    fileName: string;
    /**
     * Chunkid
     */
    chunkId?: string | null;
};

/**
 * FirestoreProductImportHeaderData
 */
export type FirestoreProductImportHeaderData = {
    /**
     * Sendername
     */
    senderName: string;
    /**
     * Sentdatetime
     */
    sentDateTime: string;
    /**
     * Senderemail
     */
    senderEmail?: string | null;
    /**
     * Sendercontactname
     */
    senderContactName?: string | null;
    /**
     * Addressees
     */
    addressees?: Array<string> | null;
};

/**
 * FirestoreProductImportImportData
 */
export type FirestoreProductImportImportData = {
    /**
     * Bucketname
     */
    bucketName: string;
    /**
     * Importid
     */
    importId: string;
    /**
     * Chunkid
     */
    chunkId?: string | null;
    /**
     * Objectname
     */
    objectName: string;
    /**
     * Foldername
     */
    folderName?: string | null;
    /**
     * Filename
     */
    fileName?: string | null;
    /**
     * Projectid
     */
    projectId: string;
    /**
     * Onixrelease
     */
    onixRelease: string;
    /**
     * Onixversion
     */
    onixVersion: string;
    /**
     * Productcount
     */
    productCount?: number | null;
    /**
     * Productglobalcount
     */
    productGlobalCount?: number | null;
    /**
     * Sourcename
     */
    sourceName?: string | null;
    /**
     * Reportingtopicname
     */
    reportingTopicName?: string | null;
    /**
     * Topicname
     */
    topicName?: string | null;
};

/**
 * FirestoreProductImportProductOnixV3Data
 */
export type FirestoreProductImportProductOnixV3Data = {
    descriptiveDetail: FirestoreProductImportProductOnixV3DataDescriptiveDetail;
    relatedMaterial?: FirestoreProductImportProductOnixV3DataRelatedMaterial | null;
    collateralDetail?: FirestoreProductImportProductOnixV3DataCollateralDetail | null;
    publishingDetail?: FirestoreProductImportProductOnixV3DataPublishingDetail | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetail
 */
export type FirestoreProductImportProductOnixV3DataCollateralDetail = {
    /**
     * Textcontents
     */
    textContents?: Array<FirestoreProductImportProductOnixV3DataCollateralDetailTextContent> | null;
    /**
     * Supportingresources
     */
    supportingResources?: Array<FirestoreProductImportProductOnixV3DataCollateralDetailSupportingResource> | null;
};

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailResourceLink
 */
export type FirestoreProductImportProductOnixV3DataCollateralDetailResourceLink = {
    /**
     * Value
     */
    value: string;
};

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailResourceVersion
 */
export type FirestoreProductImportProductOnixV3DataCollateralDetailResourceVersion = {
    /**
     * Resourcelinks
     */
    resourceLinks?: Array<FirestoreProductImportProductOnixV3DataCollateralDetailResourceLink> | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailSupportingResource
 */
export type FirestoreProductImportProductOnixV3DataCollateralDetailSupportingResource = {
    resourceContentType?: FirestoreProductImportProductOnixV3DataValueContainer | null;
    resourceMode?: FirestoreProductImportProductOnixV3DataValueContainer | null;
    /**
     * Resourceversions
     */
    resourceVersions?: Array<FirestoreProductImportProductOnixV3DataCollateralDetailResourceVersion> | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailTextContent
 */
export type FirestoreProductImportProductOnixV3DataCollateralDetailTextContent = {
    /**
     * Contentaudiences
     */
    contentAudiences: Array<FirestoreProductImportProductOnixV3DataValueContainer>;
    textType: FirestoreProductImportProductOnixV3DataValueContainer;
    /**
     * Texts
     */
    texts: Array<FirestoreProductImportProductOnixV3DataCollateralDetailTextContentText>;
};

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailTextContentText
 */
export type FirestoreProductImportProductOnixV3DataCollateralDetailTextContentText = {
    /**
     * Textformat
     */
    textformat?: string | FirestoreProductImportProductOnixV3DataCollateralDetailTextContentTextTextFormatValue | null;
    /**
     * Value
     */
    value: string;
};

/**
 * FirestoreProductImportProductOnixV3DataCollateralDetailTextContentTextTextFormatValue
 */
export type FirestoreProductImportProductOnixV3DataCollateralDetailTextContentTextTextFormatValue = {
    /**
     * Value
     */
    value: string;
};

/**
 * FirestoreProductImportProductOnixV3DataDescriptiveDetail
 */
export type FirestoreProductImportProductOnixV3DataDescriptiveDetail = {
    /**
     * Productformdetails
     */
    productFormDetails?: Array<FirestoreProductImportProductOnixV3DataValueContainer> | null;
    productForm: FirestoreProductImportProductOnixV3DataValueContainer | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetail
 */
export type FirestoreProductImportProductOnixV3DataPublishingDetail = {
    /**
     * Publishers
     */
    publishers?: Array<FirestoreProductImportProductOnixV3DataPublishingDetailPublisher> | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetailPublisher
 */
export type FirestoreProductImportProductOnixV3DataPublishingDetailPublisher = {
    /**
     * Publishernames
     */
    publisherNames?: Array<FirestoreProductImportProductOnixV3DataValueContainer> | null;
    publishingRole?: FirestoreProductImportProductOnixV3DataValueContainer | null;
    /**
     * Websites
     */
    websites?: Array<FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsite> | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsite
 */
export type FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsite = {
    websiteRole?: FirestoreProductImportProductOnixV3DataValueContainer | null;
    /**
     * Websitelinks
     */
    websiteLinks?: Array<FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsiteLink> | null;
    [key: string]: unknown;
};

/**
 * FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsiteLink
 */
export type FirestoreProductImportProductOnixV3DataPublishingDetailPublisherWebsiteLink = {
    /**
     * Value
     */
    value: string;
};

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterial
 */
export type FirestoreProductImportProductOnixV3DataRelatedMaterial = {
    /**
     * Relatedproducts
     */
    relatedProducts?: Array<FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProduct> | null;
    /**
     * Relatedworks
     */
    relatedWorks?: Array<FirestoreProductImportProductOnixV3DataRelatedMaterialWorks> | null;
};

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProduct
 */
export type FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProduct = {
    /**
     * Productidentifiers
     */
    productIdentifiers: Array<FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProductIdentifiers>;
    productForm?: FirestoreProductImportProductOnixV3DataValueContainer | null;
    /**
     * Productrelationcodes
     */
    productRelationCodes?: Array<FirestoreProductImportProductOnixV3DataValueContainer> | null;
    /**
     * Productformdetails
     */
    productFormDetails?: Array<FirestoreProductImportProductOnixV3DataValueContainer> | null;
};

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProductIdentifiers
 */
export type FirestoreProductImportProductOnixV3DataRelatedMaterialRelatedProductIdentifiers = {
    productIDType: FirestoreProductImportProductOnixV3DataValueContainer;
    idValue?: FirestoreProductImportProductOnixV3DataValueContainer | null;
    idTypeName?: FirestoreProductImportProductOnixV3DataValueContainer | null;
};

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialWorks
 */
export type FirestoreProductImportProductOnixV3DataRelatedMaterialWorks = {
    workRelationCode: FirestoreProductImportProductOnixV3DataValueContainer;
    /**
     * Workidentifiers
     */
    workIdentifiers: Array<FirestoreProductImportProductOnixV3DataRelatedMaterialWorksIdentifier>;
};

/**
 * FirestoreProductImportProductOnixV3DataRelatedMaterialWorksIdentifier
 */
export type FirestoreProductImportProductOnixV3DataRelatedMaterialWorksIdentifier = {
    idValue: FirestoreProductImportProductOnixV3DataValueContainer;
    workIDType?: FirestoreProductImportProductOnixV3DataValueContainer | null;
    idTypeName?: FirestoreProductImportProductOnixV3DataValueContainer | null;
};

/**
 * FirestoreProductImportProductOnixV3DataValueContainer
 */
export type FirestoreProductImportProductOnixV3DataValueContainer = {
    /**
     * Value
     */
    value: string;
};

/**
 * FirestoreProductManuscript
 */
export type FirestoreProductManuscript = {
    /**
     * Filename
     */
    filename: string;
    /**
     * Uploadedat
     */
    uploadedAt: Date;
    analysis?: FirestoreProductManuscriptAnalysis | null;
};

/**
 * FirestoreProductManuscriptAnalysis
 */
export type FirestoreProductManuscriptAnalysis = {
    /**
     * Result
     */
    result?: string | null;
    /**
     * Generatedcontentid
     */
    generatedContentId?: string | null;
    /**
     * Generatedat
     */
    generatedAt?: Date | null;
    /**
     * Generationrequestid
     */
    generationRequestId?: string | null;
};

/**
 * FirestoreProductPublisher
 */
export type FirestoreProductPublisher = {
    /**
     * Name
     */
    name: string;
    /**
     * Role
     */
    role: string | null;
};

/**
 * FirestoreProductQuote
 */
export type FirestoreProductQuote = {
    /**
     * Text
     */
    text?: string;
    /**
     * Source
     */
    source?: string;
};

/**
 * FirestoreProductSeries
 */
export type FirestoreProductSeries = {
    /**
     * What the publisher declared in ONIX, not a verified fact: in_series when it sent a <Collection>, not_in_series when it sent <NoCollection/>, unknown when it sent neither. Some feeds send <NoCollection/> on every product without a <Collection>.
     */
    status?: SeriesStatus;
    /**
     * Items
     */
    items?: Array<FirestoreProductSeriesItem>;
};

/**
 * FirestoreProductSeriesIdentifier
 */
export type FirestoreProductSeriesIdentifier = {
    /**
     * Type
     */
    type: string;
    /**
     * Idtypename
     */
    idTypeName?: string | null;
    /**
     * Value
     */
    value: string;
};

/**
 * FirestoreProductSeriesItem
 *
 * One ONIX <Collection> the product belongs to, as the publisher sent it.
 */
export type FirestoreProductSeriesItem = {
    /**
     * Title
     */
    title?: string | null;
    /**
     * ONIX title element level (List 149) of the collection title.
     */
    level?: SeriesLevel | null;
    /**
     * Number
     *
     * The product's position in the series, kept as sent (e.g. '01', '1 und 2', 'Prequel').
     */
    number?: string | null;
    /**
     * The ONIX element the number comes from: <CollectionSequence> or <PartNumber>.
     */
    numberSource?: SeriesNumberSource | null;
    /**
     * The <CollectionSequenceType> of the number. Null when the number comes from <PartNumber>, which declares no order, or when the type is not in ONIX List 197.
     */
    orderType?: SeriesOrderType | null;
    /**
     * Identifiers
     */
    identifiers?: Array<FirestoreProductSeriesIdentifier>;
};

/**
 * FirestoreProductSubject
 */
export type FirestoreProductSubject = {
    /**
     * Id
     *
     * The identifier of the product's subject
     */
    id?: string | null;
    /**
     * Responseid
     */
    responseId?: string | null;
    source?: SubjectSource | null;
    /**
     * Subjectcode
     */
    subjectCode?: string | null;
    /**
     * Subjectheadingtext
     */
    subjectHeadingText?: string | null;
    /**
     * Reasoning
     */
    reasoning?: string | null;
    /**
     * Subjectschemeidentifier
     */
    subjectSchemeIdentifier?: string | null;
    /**
     * Ranking
     */
    ranking?: number | null;
    /**
     * Rankingreasoning
     */
    rankingReasoning?: string | null;
    /**
     * Keywordsearchquery
     */
    keywordSearchQuery?: string | null;
    /**
     * Databaseindex
     */
    databaseIndex?: number | null;
    /**
     * Hotnessscore
     */
    hotnessScore?: number | null;
};

/**
 * FirestoreProductSubjects
 */
export type FirestoreProductSubjects = {
    /**
     * Generated
     *
     * The generated subjects
     */
    generated?: Array<FirestoreProductSubject> | null;
    /**
     * Database
     *
     * The database subjects
     */
    database?: Array<FirestoreProductSubject> | null;
    /**
     * Final
     *
     * The final keyword phrases
     */
    final?: Array<FirestoreProductSubject> | null;
    /**
     * Finalwords
     *
     * The final unique single words derived from keyword phrases
     */
    finalWords?: Array<FirestoreProductSubject> | null;
    /**
     * Bisac
     *
     * The BISAC subject headings
     */
    bisac?: Array<FirestoreProductBisacSubject>;
    [key: string]: unknown;
};

/**
 * FirestoreProductSubtitleCandidate
 */
export type FirestoreProductSubtitleCandidate = {
    /**
     * Subtitle
     */
    subtitle: string;
    /**
     * Reasoning
     */
    reasoning: string;
    /**
     * Isbestchoice
     */
    isBestChoice?: boolean | null;
    /**
     * Bestchoicereasoning
     */
    bestChoiceReasoning?: string | null;
};

/**
 * FirestoreProductSupplyDetail
 */
export type FirestoreProductSupplyDetail = {
    /**
     * Suppliername
     */
    supplierName?: string | null;
    /**
     * Supplierrole
     */
    supplierRole?: string | null;
    availability?: ProductAvailabilityNameLiteral;
};

/**
 * FirestoreProductsByTrend
 */
export type FirestoreProductsByTrend = {
    /**
     * Id
     */
    id: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Newproducts
     */
    newProducts?: Array<string>;
    trend: FirestoreTrend;
};

/**
 * FirestoreProject
 */
export type FirestoreProject = {
    type: ProjectType;
    /**
     * Name
     */
    name: string;
    /**
     * Isbns
     */
    isbns: Array<string>;
    /**
     * Createdat
     */
    createdAt: Date;
    /**
     * Userid
     */
    userId: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Searchoptions
     */
    searchOptions?: {
        [key: string]: string;
    };
    /**
     * Amplifyprojectid
     */
    amplifyProjectId?: string | null;
};

/**
 * FirestoreSearchTerm
 *
 * Model for search terms stored in Firestore - matches existing trend model structure
 */
export type FirestoreSearchTerm = {
    /**
     * Id
     */
    id: string;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Term
     */
    term: string;
    /**
     * Region
     */
    region: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Searchfrequencyrank
     */
    searchFrequencyRank: number;
    /**
     * Startdate
     */
    startDate: Date;
    trendType: SearchTermsType;
};

/**
 * FirestoreSemanticData
 */
export type FirestoreSemanticData = {
    /**
     * Id
     */
    id: string;
    /**
     * Reportid
     */
    reportId: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Productid
     */
    productId: string;
    /**
     * Workid
     */
    workId?: string | null;
    /**
     * Status
     */
    status?: string;
    /**
     * Paragraphhistory
     */
    paragraphHistory?: Array<string>;
    metadata?: SemanticDataMetadata | null;
    /**
     * Synopsis
     */
    synopsis?: string | null;
    /**
     * Genres
     */
    genres?: Array<string> | null;
    worldSettings?: SemanticDataWorldSettings | null;
    /**
     * Characters
     */
    characters?: Array<SemanticDataCharacter> | null;
    /**
     * Rawpayload
     */
    rawPayload: {
        [key: string]: unknown;
    };
    /**
     * Receivedat
     */
    receivedAt: Date;
    /**
     * Createdat
     */
    createdAt: Date;
    /**
     * Updatedat
     */
    updatedAt?: Date | null;
};

/**
 * FirestoreTrend
 */
export type FirestoreTrend = {
    /**
     * Id
     */
    id?: string | null;
    /**
     * Uuid
     */
    uuid: string;
    /**
     * Term
     */
    term: string;
    /**
     * Region
     */
    region: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Startdate
     */
    startDate: Date;
    trendType: TrendsType;
};

/**
 * FirestoreUser
 */
export type FirestoreUser = {
    /**
     * Id
     */
    id: string;
    /**
     * Userid
     */
    userId: string;
    /**
     * Email
     */
    email: string;
    /**
     * Emailverified
     */
    emailVerified?: boolean;
    /**
     * Givenname
     */
    givenName?: string | null;
    /**
     * Familyname
     */
    familyName?: string | null;
    organisation?: FirestoreOrganisationReference | null;
    /**
     * Apikeys
     */
    apiKeys?: Array<FirestoreUserApiKey> | null;
    /**
     * Roles
     */
    roles?: Array<UserRole> | null;
    /**
     * Deletedat
     */
    deletedAt?: Date | null;
};

/**
 * FirestoreUserApiKey
 */
export type FirestoreUserApiKey = {
    /**
     * Identifier
     */
    identifier: string;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Apikeyshort
     */
    apiKeyShort: string;
    /**
     * Createdat
     */
    createdAt: Date;
};

/**
 * FirestoreWeeklySearchTerm
 */
export type FirestoreWeeklySearchTerm = {
    /**
     * Id
     */
    id: string;
    /**
     * Term
     */
    term: string;
    /**
     * Current Rank
     */
    current_rank: number;
    /**
     * Movements Indicator
     */
    movements_indicator: 'up' | 'down' | 'no_change' | 'new';
    /**
     * Movement Delta
     */
    movement_delta?: number | null;
    /**
     * Frequency Rank
     */
    frequency_rank: number;
};

/**
 * FirestoreWeeklySearchTermsDoc
 */
export type FirestoreWeeklySearchTermsDoc = {
    /**
     * Id
     */
    id: string;
    /**
     * Market
     */
    market: string;
    /**
     * Week
     */
    week: string;
    /**
     * Previous Week
     */
    previous_week?: string | null;
    /**
     * Updated At
     */
    updated_at: Date;
    /**
     * Import Date
     */
    import_date: Date;
    /**
     * Terms
     */
    terms: Array<FirestoreWeeklySearchTerm>;
};

/**
 * FixedDateRule
 */
export type FixedDateRule = {
    /**
     * Type
     */
    type: 'fixed_date';
    date_kind: SeasonalEventDateKind;
    /**
     * Month
     */
    month: number;
    /**
     * Day
     */
    day: number;
};

/**
 * FixedSpanRule
 */
export type FixedSpanRule = {
    /**
     * Type
     */
    type: 'fixed_span';
    date_kind: SeasonalEventDateKind;
    /**
     * Start Month
     */
    start_month: number;
    /**
     * Start Day
     */
    start_day: number;
    /**
     * End Month
     */
    end_month: number;
    /**
     * End Day
     */
    end_day: number;
};

/**
 * GenerationEventData
 *
 * A single generation. Keyword, Thema and description generation run as
 * separate jobs, so each is its own event -- one `kind` per document. `count`
 * is how many were produced, for the popover; it is None for descriptions,
 * which always produce one.
 */
export type GenerationEventData = {
    kind: ContentKind;
    /**
     * Count
     */
    count?: number | null;
};

/**
 * GenerationMetadataEvent
 */
export type GenerationMetadataEvent = {
    /**
     * Id
     */
    id: string;
    /**
     * Productid
     */
    productId: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Occurredat
     */
    occurredAt: Date;
    meta?: MetadataEventMeta;
    /**
     * Type
     */
    type?: 'GENERATION';
    generation: GenerationEventData;
};

export type JsonValue = unknown;

/**
 * ManuscriptProcessingError
 */
export type ManuscriptProcessingError = {
    /**
     * Code
     */
    code: string;
    /**
     * Message
     */
    message: string;
};

/**
 * ManuscriptProcessingStatus
 */
export enum ManuscriptProcessingStatus {
    MATCHED = 'matched',
    QUEUED = 'queued',
    UNMATCHED = 'unmatched',
    UPLOADING = 'uploading',
    PROCESSING = 'processing',
    COMPLETE = 'complete',
    ERROR = 'error',
    SKIPPED_DUPLICATE = 'skipped_duplicate'
}

/**
 * MetadataEventMeta
 *
 * Links the event back to the source record it was emitted from.
 */
export type MetadataEventMeta = {
    /**
     * Importid
     */
    importId?: string | null;
    /**
     * Generationrequestid
     */
    generationRequestId?: string | null;
};

/**
 * NthWeekdayRule
 */
export type NthWeekdayRule = {
    /**
     * Type
     */
    type: 'nth_weekday';
    date_kind: SeasonalEventDateKind;
    /**
     * Month
     */
    month: number;
    weekday: SeasonalEventWeekday;
    /**
     * Ordinal
     */
    ordinal: -1 | 1 | 2 | 3 | 4 | 5;
};

/**
 * OnixChunkReporting
 */
export type OnixChunkReporting = {
    /**
     * Id
     */
    id: string;
    /**
     * Filename
     */
    fileName: string;
    /**
     * Errors
     */
    errors?: Array<string>;
    /**
     * Createdat
     */
    createdAt: string;
    /**
     * Success
     */
    success?: Array<OnixChunkReportingOperation>;
    /**
     * Failed
     */
    failed?: Array<OnixChunkReportingOperation>;
    /**
     * Reportid
     */
    reportId?: string | null;
    /**
     * Isbn
     */
    isbn?: string | null;
    /**
     * Errormessage
     */
    errorMessage?: string | null;
    /**
     * Timestamp
     */
    timestamp?: string | null;
    /**
     * Productid
     */
    productId?: string | null;
    status?: ChunkStatus | null;
};

/**
 * OnixChunkReportingOperation
 */
export enum OnixChunkReportingOperation {
    ONIX_PARSING = 'ONIX_PARSING',
    PRODUCT_INSERT = 'PRODUCT_INSERT'
}

/**
 * OnixExportStatus
 */
export enum OnixExportStatus {
    REQUESTED = 'REQUESTED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR'
}

/**
 * OnixImportCounts
 *
 * Counts shown in the import marker's popover: subject schemes (keywords,
 * Thema categories) plus how many descriptions the ONIX record carried.
 */
export type OnixImportCounts = {
    /**
     * Keywords
     */
    keywords?: number;
    /**
     * Themacodes
     */
    themaCodes?: number;
    /**
     * Descriptions
     */
    descriptions?: number;
};

/**
 * OnixImportEventData
 */
export type OnixImportEventData = {
    /**
     * Sourcefilename
     */
    sourceFileName?: string | null;
    /**
     * Changed
     */
    changed?: Array<ContentKind>;
    counts?: OnixImportCounts;
    /**
     * Adoption
     */
    adoption?: Array<AdoptionEntry>;
};

/**
 * OnixImportMetadataEvent
 */
export type OnixImportMetadataEvent = {
    /**
     * Id
     */
    id: string;
    /**
     * Productid
     */
    productId: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Occurredat
     */
    occurredAt: Date;
    meta?: MetadataEventMeta;
    /**
     * Type
     */
    type?: 'ONIX_IMPORT';
    onixImport: OnixImportEventData;
};

/**
 * OnixImportPipelineReporting
 */
export type OnixImportPipelineReporting = {
    /**
     * Id
     */
    id: string;
    /**
     * Bucketname
     */
    bucketName: string;
    /**
     * Objectname
     */
    objectName: string;
    /**
     * Filename
     */
    fileName: string;
    /**
     * Createdat
     */
    createdAt: string;
    /**
     * Foldername
     */
    folderName: string;
    organisation: FirestoreOrganisationReference;
    /**
     * Mode
     */
    mode?: string | null;
    status?: PipelineImportReportStatus;
    /**
     * Totalfiles
     */
    totalFiles?: number;
    /**
     * Filescreatedfromsplitcount
     */
    filesCreatedFromSplitCount?: number | null;
    /**
     * Filesuploadedcount
     */
    filesUploadedCount?: number | null;
    /**
     * Tasksscheduledsuccessfully
     */
    tasksScheduledSuccessfully?: number;
    /**
     * Tasksscheduledfailed
     */
    tasksScheduledFailed?: number;
    /**
     * Haserrors
     */
    hasErrors?: boolean;
    /**
     * Errorcount
     */
    errorCount?: number;
    /**
     * Lasterror
     */
    lastError?: string | null;
    /**
     * Lastupdated
     */
    lastUpdated?: string | null;
    /**
     * Completedat
     */
    completedAt?: string | null;
    /**
     * Productssucceeded
     */
    productsSucceeded?: number;
    /**
     * Productsfailed
     */
    productsFailed?: number;
    /**
     * Progress
     */
    progress?: number;
};

/**
 * OpenAIStatus
 */
export enum OpenAiStatus {
    VALIDATING = 'VALIDATING',
    FAILED = 'FAILED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    EXPIRED = 'EXPIRED',
    CANCELLED = 'CANCELLED',
    CANCELLING = 'CANCELLING'
}

export enum ProductAvailabilityCodeLiteral {
    _01 = '01',
    _09 = '09',
    _10 = '10',
    _11 = '11',
    _12 = '12',
    _20 = '20',
    _21 = '21',
    _22 = '22',
    _23 = '23',
    _30 = '30',
    _31 = '31',
    _32 = '32',
    _33 = '33',
    _34 = '34',
    _40 = '40',
    _41 = '41',
    _42 = '42',
    _43 = '43',
    _44 = '44',
    _45 = '45',
    _46 = '46',
    _47 = '47',
    _48 = '48',
    _49 = '49',
    _50 = '50',
    _51 = '51',
    _52 = '52',
    _97 = '97',
    _98 = '98',
    _99 = '99'
}

export enum ProductAvailabilityNameLiteral {
    CANCELLED = 'Cancelled',
    NOT_YET_AVAILABLE_POSTPONED_INDEFINITELY = 'Not_yet_available_postponed_indefinitely',
    NOT_YET_AVAILABLE = 'Not_yet_available',
    AWAITING_STOCK = 'Awaiting_stock',
    NOT_YET_AVAILABLE_WILL_BE_POD = 'Not_yet_available_will_be_POD',
    AVAILABLE = 'Available',
    IN_STOCK = 'In_stock',
    TO_ORDER = 'To_order',
    POD = 'POD',
    TEMPORARILY_UNAVAILABLE = 'Temporarily_unavailable',
    OUT_OF_STOCK = 'Out_of_stock',
    REPRINTING = 'Reprinting',
    AWAITING_REISSUE = 'Awaiting_reissue',
    TEMPORARILY_WITHDRAWN_FROM_SALE = 'Temporarily_withdrawn_from_sale',
    NOT_AVAILABLE_REASON_UNSPECIFIED = 'Not_available_reason_unspecified',
    NOT_AVAILABLE_REPLACED_BY_NEW_PRODUCT = 'Not_available_replaced_by_new_product',
    NOT_AVAILABLE_OTHER_FORMAT_AVAILABLE = 'Not_available_other_format_available',
    NO_LONGER_SUPPLIED_BY_THE_SUPPLIER = 'No_longer_supplied_by_the_supplier',
    APPLY_DIRECT = 'Apply_direct',
    NOT_SOLD_SEPARATELY = 'Not_sold_separately',
    WITHDRAWN_FROM_SALE = 'Withdrawn_from_sale',
    REMAINDERED = 'Remaindered',
    NOT_AVAILABLE_REPLACED_BY_POD = 'Not_available_replaced_by_POD',
    RECALLED = 'Recalled',
    NOT_SOLD_AS_SET = 'Not_sold_as_set',
    NOT_AVAILABLE_PUBLISHER_INDICATES_OP = 'Not_available_publisher_indicates_OP',
    NOT_AVAILABLE_PUBLISHER_NO_LONGER_SELLS_PRODUCT_IN_THIS_MARKET = 'Not_available_publisher_no_longer_sells_product_in_this_market',
    NO_RECENT_UPDATE_RECEIVED = 'No_recent_update_received',
    NO_LONGER_RECEIVING_UPDATES = 'No_longer_receiving_updates',
    CONTACT_SUPPLIER = 'Contact_supplier'
}

export enum ProductFormCodeLiteral {
    _00 = '00',
    AA = 'AA',
    AB = 'AB',
    AC = 'AC',
    AD = 'AD',
    AE = 'AE',
    AF = 'AF',
    AG = 'AG',
    AH = 'AH',
    AI = 'AI',
    AJ = 'AJ',
    AK = 'AK',
    AL = 'AL',
    AM = 'AM',
    AN = 'AN',
    AO = 'AO',
    AZ = 'AZ',
    BA = 'BA',
    BB = 'BB',
    BC = 'BC',
    BD = 'BD',
    BE = 'BE',
    BF = 'BF',
    BG = 'BG',
    BH = 'BH',
    BI = 'BI',
    BJ = 'BJ',
    BK = 'BK',
    BL = 'BL',
    BM = 'BM',
    BN = 'BN',
    BO = 'BO',
    BP = 'BP',
    BZ = 'BZ',
    CA = 'CA',
    CB = 'CB',
    CC = 'CC',
    CD = 'CD',
    CE = 'CE',
    CZ = 'CZ',
    DA = 'DA',
    DB = 'DB',
    DC = 'DC',
    DE = 'DE',
    DF = 'DF',
    DI = 'DI',
    DJ = 'DJ',
    DK = 'DK',
    DL = 'DL',
    DM = 'DM',
    DN = 'DN',
    DO = 'DO',
    DZ = 'DZ',
    EA = 'EA',
    EB = 'EB',
    EC = 'EC',
    ED = 'ED',
    FA = 'FA',
    FC = 'FC',
    FD = 'FD',
    FE = 'FE',
    FF = 'FF',
    FZ = 'FZ',
    LA = 'LA',
    LB = 'LB',
    LC = 'LC',
    MA = 'MA',
    MB = 'MB',
    MC = 'MC',
    MZ = 'MZ',
    PA = 'PA',
    PB = 'PB',
    PC = 'PC',
    PD = 'PD',
    PE = 'PE',
    PF = 'PF',
    PG = 'PG',
    PH = 'PH',
    PI = 'PI',
    PJ = 'PJ',
    PK = 'PK',
    PL = 'PL',
    PM = 'PM',
    PN = 'PN',
    PO = 'PO',
    PP = 'PP',
    PQ = 'PQ',
    PR = 'PR',
    PS = 'PS',
    PT = 'PT',
    PU = 'PU',
    PV = 'PV',
    PZ = 'PZ',
    SA = 'SA',
    SB = 'SB',
    SC = 'SC',
    SD = 'SD',
    SE = 'SE',
    SF = 'SF',
    SG = 'SG',
    VA = 'VA',
    VF = 'VF',
    VI = 'VI',
    VJ = 'VJ',
    VK = 'VK',
    VL = 'VL',
    VM = 'VM',
    VN = 'VN',
    VO = 'VO',
    VP = 'VP',
    VQ = 'VQ',
    VZ = 'VZ',
    XA = 'XA',
    XB = 'XB',
    XC = 'XC',
    XD = 'XD',
    XE = 'XE',
    XF = 'XF',
    XG = 'XG',
    XH = 'XH',
    XI = 'XI',
    XJ = 'XJ',
    XK = 'XK',
    XL = 'XL',
    XM = 'XM',
    XN = 'XN',
    XO = 'XO',
    XY = 'XY',
    XZ = 'XZ',
    ZA = 'ZA',
    ZB = 'ZB',
    ZC = 'ZC',
    ZD = 'ZD',
    ZE = 'ZE',
    ZF = 'ZF',
    ZG = 'ZG',
    ZH = 'ZH',
    ZI = 'ZI',
    ZJ = 'ZJ',
    ZK = 'ZK',
    ZL = 'ZL',
    ZM = 'ZM',
    ZN = 'ZN',
    ZO = 'ZO',
    ZP = 'ZP',
    ZX = 'ZX',
    ZY = 'ZY',
    ZZ = 'ZZ'
}

export enum ProductFormDescLiteral {
    UNDEFINED = 'Undefined',
    AUDIO = 'Audio',
    AUDIO_CASSETTE = 'Audio cassette',
    CD_AUDIO = 'CD-Audio',
    DAT = 'DAT',
    AUDIO_DISC = 'Audio disc',
    AUDIO_TAPE = 'Audio tape',
    MINI_DISC = 'MiniDisc',
    CD_EXTRA = 'CD-Extra',
    DVD_AUDIO = 'DVD Audio',
    DOWNLOADABLE_AUDIO_FILE = 'Downloadable audio file',
    PRE_RECORDED_DIGITAL_AUDIO_PLAYER = 'Pre-recorded digital audio player',
    PRE_RECORDED_SD_CARD = 'Pre-recorded SD card',
    LP = 'LP',
    DOWNLOADABLE_AND_ONLINE_AUDIO_FILE = 'Downloadable and online audio file',
    ONLINE_AUDIO_FILE = 'Online audio file',
    OTHER_AUDIO_FORMAT = 'Other audio format',
    BOOK = 'Book',
    HARDBACK = 'Hardback',
    PAPERBACK_SOFTBACK = 'Paperback / softback',
    LOOSE_LEAF = 'Loose-leaf',
    SPIRAL_BOUND = 'Spiral bound',
    PAMPHLET = 'Pamphlet',
    LEATHER_FINE_BINDING = 'Leather / fine binding',
    BOARD_BOOK = 'Board book',
    RAG_BOOK = 'Rag book',
    BATH_BOOK = 'Bath book',
    NOVELTY_BOOK = 'Novelty book',
    SLIDE_BOUND = 'Slide bound',
    BIG_BOOK = 'Big book',
    PART_WORK_FASCÍCULO = 'Part-work (fascículo)',
    FOLD_OUT_BOOK_OR_CHART = 'Fold-out book or chart',
    FOAM_BOOK = 'Foam book',
    OTHER_BOOK_FORMAT = 'Other book format',
    SHEET_MAP = 'Sheet map',
    'SHEET_MAP,_FOLDED' = 'Sheet map, folded',
    'SHEET_MAP,_FLAT' = 'Sheet map, flat',
    'SHEET_MAP,_ROLLED' = 'Sheet map, rolled',
    GLOBE = 'Globe',
    OTHER_CARTOGRAPHIC = 'Other cartographic',
    DIGITAL_ON_PHYSICAL_CARRIER = 'Digital (on physical carrier)',
    CD_ROM = 'CD-ROM',
    CD_I = 'CD-I',
    GAME_CARTRIDGE = 'Game cartridge',
    DISKETTE = 'Diskette',
    DVD_ROM = 'DVD-ROM',
    SECURE_DIGITAL_SD_MEMORY_CARD = 'Secure Digital (SD) Memory Card',
    COMPACT_FLASH_MEMORY_CARD = 'Compact Flash Memory Card',
    MEMORY_STICK_MEMORY_CARD = 'Memory Stick Memory Card',
    USB_FLASH_DRIVE = 'USB Flash Drive',
    DOUBLE_SIDED_CD_DVD = 'Double-sided CD/DVD',
    BR_ROM = 'BR-ROM',
    OTHER_DIGITAL_CARRIER = 'Other digital carrier',
    DIGITAL_DELIVERED_ELECTRONICALLY = 'Digital (delivered electronically)',
    DIGITAL_DOWNLOAD_AND_ONLINE = 'Digital download and online',
    DIGITAL_ONLINE = 'Digital online',
    DIGITAL_DOWNLOAD = 'Digital download',
    FILM_OR_TRANSPARENCY = 'Film or transparency',
    SLIDES = 'Slides',
    OHP_TRANSPARENCIES = 'OHP transparencies',
    FILMSTRIP = 'Filmstrip',
    FILM = 'Film',
    OTHER_FILM_OR_TRANSPARENCY_FORMAT = 'Other film or transparency format',
    DIGITAL_PRODUCT_LICENSE = 'Digital product license',
    DIGITAL_PRODUCT_LICENSE_KEY = 'Digital product license key',
    DIGITAL_PRODUCT_LICENSE_CODE = 'Digital product license code',
    MICROFORM = 'Microform',
    MICROFICHE = 'Microfiche',
    MICROFILM = 'Microfilm',
    OTHER_MICROFORM = 'Other microform',
    MISCELLANEOUS_PRINT = 'Miscellaneous print',
    ADDRESS_BOOK = 'Address book',
    CALENDAR = 'Calendar',
    CARDS = 'Cards',
    COPYMASTERS = 'Copymasters',
    DIARY_OR_JOURNAL = 'Diary or journal',
    FRIEZE = 'Frieze',
    KIT = 'Kit',
    SHEET_MUSIC = 'Sheet music',
    POSTCARD_BOOK_OR_PACK = 'Postcard book or pack',
    POSTER = 'Poster',
    RECORD_BOOK = 'Record book',
    WALLET_OR_FOLDER = 'Wallet or folder',
    PICTURES_OR_PHOTOGRAPHS = 'Pictures or photographs',
    WALLCHART = 'Wallchart',
    STICKERS = 'Stickers',
    PLATE_LÁMINA = 'Plate (lámina)',
    NOTEBOOK_BLANK_BOOK = 'Notebook / blank book',
    ORGANIZER = 'Organizer',
    BOOKMARK = 'Bookmark',
    LEAFLET = 'Leaflet',
    BOOK_PLATES = 'Book plates',
    OTHER_PRINTED_ITEM = 'Other printed item',
    MULTIPLE_COMPONENT_RETAIL_PRODUCT = 'Multiple-component retail product',
    'MULTIPLE_COMPONENT_RETAIL_PRODUCT,_BOXED' = 'Multiple-component retail product, boxed',
    'MULTIPLE_COMPONENT_RETAIL_PRODUCT,_SLIP_CASED' = 'Multiple-component retail product, slip-cased',
    'MULTIPLE_COMPONENT_RETAIL_PRODUCT,_SHRINK_WRAPPED' = 'Multiple-component retail product, shrink-wrapped',
    'MULTIPLE_COMPONENT_RETAIL_PRODUCT,_LOOSE' = 'Multiple-component retail product, loose',
    'MULTIPLE_COMPONENT_RETAIL_PRODUCT,_PART_S_ENCLOSED' = 'Multiple-component retail product, part(s) enclosed',
    'MULTIPLE_COMPONENT_RETAIL_PRODUCT,_ENTIRELY_DIGITAL' = 'Multiple-component retail product, entirely digital',
    VIDEO = 'Video',
    VIDEODISC = 'Videodisc',
    DVD_VIDEO = 'DVD video',
    VHS_VIDEO = 'VHS video',
    BETAMAX_VIDEO = 'Betamax video',
    VCD = 'VCD',
    SVCD = 'SVCD',
    HD_DVD = 'HD DVD',
    BLU_RAY = 'Blu-ray',
    UMD_VIDEO = 'UMD Video',
    CBHD = 'CBHD',
    OTHER_VIDEO_FORMAT = 'Other video format',
    TRADE_ONLY_MATERIAL = 'Trade-only material',
    'DUMPBIN –_EMPTY' = 'Dumpbin – empty',
    'DUMPBIN –_FILLED' = 'Dumpbin – filled',
    'COUNTERPACK –_EMPTY' = 'Counterpack – empty',
    'COUNTERPACK –_FILLED' = 'Counterpack – filled',
    'POSTER,_PROMOTIONAL' = 'Poster, promotional',
    SHELF_STRIP = 'Shelf strip',
    WINDOW_PIECE = 'Window piece',
    STREAMER = 'Streamer',
    'SPINNER –_EMPTY' = 'Spinner – empty',
    LARGE_BOOK_DISPLAY = 'Large book display',
    SHRINK_WRAPPED_PACK = 'Shrink-wrapped pack',
    BOXED_PACK = 'Boxed pack',
    PACK_OUTER_PACKAGING_UNSPECIFIED = 'Pack (outer packaging unspecified)',
    'SPINNER –_FILLED' = 'Spinner – filled',
    'OTHER_POINT_OF_SALE –_INCLUDING_RETAIL_PRODUCT' = 'Other point of sale – including retail product',
    OTHER_POINT_OF_SALE = 'Other point of sale',
    GENERAL_MERCHANDISE = 'General merchandise',
    DOLL_OR_FIGURE = 'Doll or figure',
    SOFT_TOY = 'Soft toy',
    TOY = 'Toy',
    GAME = 'Game',
    T_SHIRT = 'T-shirt',
    E_BOOK_READER = 'E-book reader',
    TABLET_COMPUTER = 'Tablet computer',
    AUDIOBOOK_PLAYER = 'Audiobook player',
    JIGSAW = 'Jigsaw',
    MUG = 'Mug',
    TOTE_BAG = 'Tote bag',
    TABLEWARE = 'Tableware',
    UMBRELLA = 'Umbrella',
    'PAINTS,_CRAYONS,_PENCILS' = 'Paints, crayons, pencils',
    HANDICRAFT_KIT = 'Handicraft kit',
    OTHER_TOY_GAME_ACCESSORIES = 'Other toy/game accessories',
    OTHER_APPAREL = 'Other apparel',
    OTHER_MERCHANDISE = 'Other merchandise'
}

/**
 * PROJECT_TYPE
 */
export enum ProjectType {
    ISBNS = 'isbns',
    GOOGLE_TRENDS = 'google-trends',
    AMAZON_TRENDS = 'amazon-trends'
}

/**
 * PipelineImportReport
 */
export type PipelineImportReport = {
    /**
     * Importid
     */
    importId: string;
    /**
     * Bucketname
     */
    bucketName: string;
    /**
     * Objectname
     */
    objectName: string;
    /**
     * Filename
     */
    fileName: string;
    /**
     * Createdat
     */
    createdAt: string;
    /**
     * Foldername
     */
    folderName: string;
    error?: PipelineImportReportError | null;
    /**
     * Errors
     */
    errors?: Array<PipelineImportReportError>;
    /**
     * Projectid
     */
    projectId: string;
    /**
     * Completedat
     */
    completedAt?: string | null;
    /**
     * Globalisbns
     */
    globalIsbns?: Array<string>;
    /**
     * Importedisbns
     */
    importedIsbns?: Array<string>;
    /**
     * Filteredisbns
     */
    filteredIsbns?: Array<string>;
    /**
     * Failedisbns
     */
    failedIsbns?: Array<string>;
    /**
     * Productsglobalcount
     */
    productsGlobalCount: number;
    /**
     * Productsfilteredcount
     */
    productsFilteredCount: number;
    /**
     * Productsimportedcount
     */
    productsImportedCount: number;
    /**
     * Productsfailedcount
     */
    productsFailedCount: number;
    /**
     * Success
     */
    success: boolean;
    /**
     * Description
     */
    description?: string;
    organisation: FirestoreOrganisationReference;
};

/**
 * PipelineImportReportError
 */
export type PipelineImportReportError = {
    /**
     * Level
     */
    level?: string | null;
    /**
     * Validator
     */
    validator?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Message
     */
    message?: string | null;
    /**
     * Error Type
     */
    error_type?: string | null;
};

/**
 * PipelineImportReportStatus
 */
export enum PipelineImportReportStatus {
    PROCESSING = 'processing',
    COMPLETED = 'completed',
    FAILED = 'failed',
    TIMEOUT = 'timeout'
}

/**
 * ProductRaw
 *
 * Raw projection of a productsImport document.
 *
 * The three section dicts are 1:1 dumps of the corresponding subtrees on
 * ``FirestoreProductImport`` (``model_dump(by_alias=True, exclude_none=True)``).
 * The shape is intentionally not re-modeled here so the wire stays in sync
 * with what the import pipeline actually writes; consumers can rely on
 * Firestore field names directly.
 */
export type ProductRaw = {
    /**
     * Id
     */
    id: string;
    /**
     * Header
     */
    header?: {
        [key: string]: unknown;
    };
    /**
     * Baseproduct
     */
    baseProduct?: {
        [key: string]: unknown;
    };
    /**
     * Rawonixv3
     */
    rawOnixV3?: {
        [key: string]: unknown;
    };
};

/**
 * ProductSource
 */
export enum ProductSource {
    ONIX_IMPORT = 'ONIX_IMPORT',
    MANUSCRIPT_ONLY = 'MANUSCRIPT_ONLY'
}

/**
 * SEARCH_TERMS_TYPE
 */
export enum SearchTermsType {
    AMAZON = 'AMAZON'
}

/**
 * SeasonalEvent
 */
export type SeasonalEvent = {
    /**
     * Event Id
     */
    event_id: string;
    /**
     * Display Name
     */
    display_name: string;
    region: SeasonalEventRegion;
    /**
     * Lead Time Months
     */
    lead_time_months: number;
    /**
     * Date Rule
     */
    date_rule: ({
        type: 'fixed_date';
    } & FixedDateRule) | ({
        type: 'nth_weekday';
    } & NthWeekdayRule) | ({
        type: 'easter_offset';
    } & EasterOffsetRule) | ({
        type: 'fixed_span';
    } & FixedSpanRule) | null;
    /**
     * Occurrences
     */
    occurrences?: Array<SeasonalEventOccurrence>;
    /**
     * Overrides
     */
    overrides?: Array<SeasonalEventOverride>;
    /**
     * Prompts
     */
    prompts?: Array<SeasonalEventPrompt>;
    provenance?: SeasonalEventProvenance | null;
    /**
     * Categories
     */
    categories?: Array<string>;
    /**
     * Evidence
     */
    evidence?: Array<string>;
    /**
     * Judge Reasoning
     */
    judge_reasoning?: string | null;
    international_match?: SeasonalEventInternationalMatch | null;
};

/**
 * SeasonalEventDateKind
 */
export enum SeasonalEventDateKind {
    ACTUAL = 'actual',
    OBSERVED = 'observed',
    CAMPAIGN = 'campaign'
}

/**
 * SeasonalEventDateSource
 */
export type SeasonalEventDateSource = {
    /**
     * Type
     */
    type: 'generated' | 'curated';
    /**
     * Reference
     */
    reference: string;
    /**
     * Url
     */
    url?: string | null;
    /**
     * Checked At
     */
    checked_at?: Date | null;
};

/**
 * SeasonalEventInternationalMatch
 *
 * The International event that a regional event replaces for its marketplace.
 */
export type SeasonalEventInternationalMatch = {
    /**
     * Event Id
     */
    event_id: string;
    /**
     * Method
     */
    method: 'slug' | 'alias' | 'name';
};

/**
 * SeasonalEventOccurrence
 */
export type SeasonalEventOccurrence = {
    /**
     * Year
     */
    year: number;
    date_kind: SeasonalEventDateKind;
    /**
     * Start
     */
    start: Date;
    /**
     * End
     */
    end: Date;
    source: SeasonalEventDateSource;
};

/**
 * SeasonalEventOverride
 */
export type SeasonalEventOverride = {
    /**
     * Year
     */
    year: number;
    date_kind: SeasonalEventDateKind;
    /**
     * Action
     */
    action: 'replace' | 'suppress';
    /**
     * Start
     */
    start?: Date | null;
    /**
     * End
     */
    end?: Date | null;
    source: SeasonalEventDateSource;
};

/**
 * SeasonalEventPrompt
 */
export type SeasonalEventPrompt = {
    /**
     * Prompt Id
     */
    prompt_id: string;
    /**
     * Prompt Revision
     */
    prompt_revision: number;
    /**
     * Text
     */
    text: string;
    provenance?: SeasonalEventProvenance | null;
};

/**
 * SeasonalEventProvenance
 */
export type SeasonalEventProvenance = {
    /**
     * Pipeline Version
     */
    pipeline_version: string;
    /**
     * Template Version
     */
    template_version: string;
    /**
     * Model
     */
    model: string;
    /**
     * Parameters
     */
    parameters: {
        [key: string]: JsonValue;
    };
    /**
     * Inputs
     */
    inputs: {
        [key: string]: JsonValue;
    };
    /**
     * Generated At
     */
    generated_at: Date;
    /**
     * Langfuse Trace Id
     */
    langfuse_trace_id?: string | null;
};

/**
 * SeasonalEventRegion
 */
export enum SeasonalEventRegion {
    US = 'us',
    UK = 'uk',
    DE = 'de',
    AUS = 'aus',
    INTERNATIONAL = 'international'
}

/**
 * SeasonalEventWeekday
 */
export enum SeasonalEventWeekday {
    MONDAY = 'monday',
    TUESDAY = 'tuesday',
    WEDNESDAY = 'wednesday',
    THURSDAY = 'thursday',
    FRIDAY = 'friday',
    SATURDAY = 'saturday',
    SUNDAY = 'sunday'
}

/**
 * SemanticDataCharacter
 */
export type SemanticDataCharacter = {
    /**
     * Name
     */
    name?: string | null;
    /**
     * Age
     */
    age?: string | number | null;
    /**
     * Pronouns
     */
    pronouns?: string | null;
    /**
     * Role
     */
    role?: string | null;
    /**
     * Description
     */
    description?: string | null;
    /**
     * Aliases
     */
    aliases?: Array<string> | null;
};

/**
 * SemanticDataMetadata
 */
export type SemanticDataMetadata = {
    /**
     * Title
     */
    title?: string | null;
    /**
     * Author
     */
    author?: string | null;
    /**
     * Language
     */
    language?: string | null;
};

/**
 * SemanticDataWorldSetting
 */
export type SemanticDataWorldSetting = {
    /**
     * Settingtype
     */
    settingType?: string | null;
    /**
     * Location
     */
    location?: string | null;
    /**
     * Timeperiod
     */
    timePeriod?: string | null;
    /**
     * Locationlabel
     */
    locationLabel?: string | null;
    /**
     * Timeperiodlabel
     */
    timePeriodLabel?: string | null;
    /**
     * Localsocialstructure
     */
    localSocialStructure?: string | null;
};

/**
 * SemanticDataWorldSettings
 */
export type SemanticDataWorldSettings = {
    /**
     * Settings
     */
    settings?: Array<SemanticDataWorldSetting> | null;
    /**
     * Socialstructure
     */
    socialStructure?: string | null;
    /**
     * Metaphysics
     */
    metaphysics?: string | null;
    /**
     * Socialstructurelabel
     */
    socialStructureLabel?: string | null;
    /**
     * Metaphysicslabel
     */
    metaphysicsLabel?: string | null;
};

/**
 * SeriesLevel
 */
export enum SeriesLevel {
    COLLECTION = 'collection',
    SUBCOLLECTION = 'subcollection'
}

/**
 * SeriesNumberSource
 */
export enum SeriesNumberSource {
    COLLECTION_SEQUENCE = 'collection_sequence',
    PART_NUMBER = 'part_number'
}

/**
 * SeriesOrderType
 *
 * ONIX List 197 (collection sequence type).
 */
export enum SeriesOrderType {
    PROPRIETARY = 'proprietary',
    TITLE_ORDER = 'title_order',
    PUBLICATION_ORDER = 'publication_order',
    TEMPORAL_NARRATIVE_ORDER = 'temporal_narrative_order',
    ORIGINAL_PUBLICATION_ORDER = 'original_publication_order',
    SUGGESTED_READING_ORDER = 'suggested_reading_order',
    SUGGESTED_DISPLAY_ORDER = 'suggested_display_order'
}

/**
 * SeriesStatus
 */
export enum SeriesStatus {
    IN_SERIES = 'in_series',
    NOT_IN_SERIES = 'not_in_series',
    UNKNOWN = 'unknown'
}

/**
 * SharedData
 */
export type SharedData = {
    /**
     * Defaulttargetgroups
     */
    defaultTargetGroups?: Array<string>;
    /**
     * Productavailability
     */
    ProductAvailability?: {
        [key: string]: string;
    };
    /**
     * Themamajorsubjectcodes
     *
     * List of major THEMA subject category codes
     */
    themaMajorSubjectCodes?: Array<string>;
    /**
     * Themamajorqualifiercodes
     *
     * List of major THEMA qualifier category codes
     */
    themaMajorQualifierCodes?: Array<string>;
};

/**
 * SubjectSource
 */
export enum SubjectSource {
    GENERATED = 'GENERATED',
    DATABASE = 'DATABASE',
    MANUAL = 'MANUAL'
}

/**
 * TRENDS_TYPE
 */
export enum TrendsType {
    GOOGLE = 'GOOGLE',
    AMAZON = 'AMAZON'
}

/**
 * UserRole
 */
export enum UserRole {
    SUPER_ADMIN = 'SUPER_ADMIN',
    ADMIN = 'ADMIN',
    MEMBER = 'MEMBER'
}
